import { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { StateType } from "../../../backend/store";
import { authTriggerActions } from "../../../backend/store/reducers/authReducer";
import { useTranslation } from "../../../hooks/useTranslation";

/**
 * Shown when a logout is attempted with writes still queued.
 *
 * A queued write belongs to the person who made it — it replays with their token
 * against their workspace — so it cannot simply be handed to whoever logs in next,
 * and silently discarding it would throw away field work the volunteer was told
 * had been saved. Warning and letting them decide is the only honest option.
 */
export const PendingLogoutDialog: FC = () => {
  const { p } = useTranslation("offline");
  const dispatch = useDispatch();
  const pending = useSelector((state: StateType) => state.authStore.pendingLogoutWarning);

  if (!pending) return null;

  const dismiss = () => dispatch(authTriggerActions.setPendingLogoutWarningAC(null));

  return (
    <Dialog open onClose={dismiss} data-testid="pending-logout-dialog">
      <DialogTitle>{p("logout_pending_title")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{p("logout_pending_body", { count: pending })}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={dismiss} data-testid="pending-logout-stay">
          {p("logout_pending_stay")}
        </Button>
        <Button
          color="warning"
          data-testid="pending-logout-anyway"
          onClick={() => {
            dispatch(authTriggerActions.setPendingLogoutWarningAC(null));
            dispatch(authTriggerActions.logoutUserTriggerAC(true));
          }}
        >
          {p("logout_pending_anyway")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
